import type { RowSelectionState, Table } from "@tanstack/react-table";
import * as React from "react";
import type { DataTableRow } from "./data-table.types";

interface UseSelectAllOptions<T extends object> {
  table: Table<DataTableRow<T>>;
  maxSelectableRows?: number;
  setRowSelection?: (selection: RowSelectionState) => void;
}

interface UseSelectAllResult {
  isAllSelected: boolean;
  isSomeSelected: boolean;
  hasSelectableRows: boolean;
  toggleSelectAll: (checked: boolean) => void;
}

/**
 * Select-all state shared by the header checkbox and the labelled select-all row.
 *
 * Works off the pre-pagination row model so the toggle covers every page rather than
 * just the visible one, skips grouped and disabled rows, and stops at
 * `maxSelectableRows` when the consumer caps selection.
 */
export function useSelectAll<T extends object>({ table, maxSelectableRows, setRowSelection }: UseSelectAllOptions<T>): UseSelectAllResult {
  const prePaginationFlatRows = table.getPrePaginationRowModel().flatRows;
  const allLeafRows = React.useMemo(
    () => prePaginationFlatRows.filter((row) => !row.getIsGrouped() && !row.original.isDisabled),
    [prePaginationFlatRows]
  );

  const effectiveMax = maxSelectableRows ? Math.min(allLeafRows.length, maxSelectableRows) : allLeafRows.length;

  const rowSelection = table.getState().rowSelection;
  const selectedLeafCount = React.useMemo(
    () => allLeafRows.filter((row) => rowSelection[row.id] === true).length,
    [allLeafRows, rowSelection]
  );

  const toggleSelectAll = React.useCallback(
    (checked: boolean) => {
      if (!setRowSelection) return;
      if (!checked) {
        setRowSelection({});
        return;
      }
      const newSelection: RowSelectionState = {};
      for (const row of allLeafRows.slice(0, effectiveMax)) {
        newSelection[row.id] = true;
      }
      setRowSelection(newSelection);
    },
    [allLeafRows, effectiveMax, setRowSelection]
  );

  return {
    isAllSelected: selectedLeafCount > 0 && selectedLeafCount >= effectiveMax,
    isSomeSelected: selectedLeafCount > 0 && selectedLeafCount < effectiveMax,
    hasSelectableRows: allLeafRows.length > 0,
    toggleSelectAll,
  };
}
